import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/utils';
import { LucideIcon, Loader2 } from 'lucide-react';

interface DashboardButtonProps extends Omit<HTMLMotionProps<"button">, 'children'> {
  children: React.ReactNode;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: LucideIcon;
  'data-test-id'?: string;
}

const DashboardButton = ({ 
  children, 
  className, 
  size = "default", 
  type = "button",
  onClick,
  disabled = false,
  loading = false,
  icon: Icon,
  'data-test-id': testId,
  ...props 
}: DashboardButtonProps) => {
  return (
    <motion.button
      type={type}
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      onClick={onClick}
      disabled={disabled || loading}
      data-test-id={testId}
      className={cn(
        "relative overflow-hidden",
        "bg-brand-gradient",
        "border-none",
        "shadow-lg",
        "transform-gpu",
        "transition-all duration-300 ease-out",
        "hover:shadow-xl hover:shadow-primary/20",
        "hover:brightness-110",
        "text-white font-medium",
        "flex items-center justify-center gap-2",
        size === "sm" ? "px-3 py-1.5 text-sm h-8" : 
        size === "lg" ? "px-6 py-3 text-lg h-12" : 
        size === "icon" ? "p-2 h-8 w-8" : "px-4 py-2 h-10",
        "rounded-xl",
        (disabled || loading) && "opacity-70 cursor-not-allowed",
        className
      )}
      {...props}
    >
      {/* Shine effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] hover:translate-x-[100%]" />
      
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center gap-2">
        {loading ? (
          <>
            <Loader2 size={size === "sm" ? 14 : size === "lg" ? 20 : 16} className="animate-spin" />
            <span>{typeof children === 'string' ? 'Loading...' : children}</span>
          </>
        ) : (
          <>
            {Icon && <Icon size={size === "sm" ? 14 : size === "lg" ? 20 : 16} />}
            <span>{children}</span>
          </>
        )}
      </div>
    </motion.button>
  );
};

DashboardButton.displayName = 'DashboardButton';

export default DashboardButton;
